/**
 * try.veldon.cz — the REAL Veldon CMS running in BROWSER-ONLY mode.
 *
 * The signature mechanic is the LIVE EDIT: the REAL Veldon engine
 * (renderResolvedSite, via DemoStore) renders the HŘEBEN snapshot into a
 * style-isolated preview iframe; every edit goes through the SAME engine
 * dispatchers the server uses — module edits via store.applyOperation (the real
 * module.operations[...] path) and content/text/image/gallery edits via
 * store.setContentValue (the real typed values model) — each validated by the
 * real validateSnapshotAgainstDefinition before it commits. Inline copy is
 * contenteditable and synced back on blur; the pure re-render swaps the preview.
 * The snapshot is always authoritative, so an inline edit and a re-render never
 * disagree. There is no theme/accent/palette control because the engine has no
 * such operation — a fabricated capability is impossible.
 *
 * Everything persists to the visitor's localStorage only (the sole difference
 * from the server: DB -> localStorage). The site is LIGHT-ONLY, like veldon.cz.
 * A share link compresses the snapshot into the URL hash (zero backend). The
 * gift-card / checkout is an HONEST simulated showcase: it visibly does not
 * charge and lands on a roadmap state.
 *
 * DOM is built from app-authored template strings; every dynamic/user value is
 * routed through esc() before interpolation, and markup is written via
 * setHTML() (replaceChildren + insertAdjacentHTML) so no untrusted string ever
 * reaches an HTML sink unescaped.
 */
import { definition, snapshot } from './venue.js';
import { VENUE_CSS } from './venue-css.js';
import { DemoStore } from './store.js';
import { S } from './strings.js';
import type { ContentSnapshotT, JsonValueT, SiteDefinitionT } from './engine.js';

interface CuratedImage {
  src: string;
  thumb: string;
  alt: string;
}

interface FieldMeta {
  sectionId: string;
  type: string;
}

/** A shop product as read back from the snapshot (commerce.catalog data). The
 *  authoritative shape lives in the engine's Product schema; this is the narrow
 *  view the demo UI reads — every WRITE still goes through store.applyOperation,
 *  which validates against that real schema. */
interface ShopProduct {
  id: string;
  name: string;
  description?: string;
  price: { amountMinor: number; currency: string };
  image?: { src: string; alt: string };
  available: boolean;
  kind: 'standard' | 'gift_card';
  giftCard?: { presetAmountsMinor?: number[] };
}
const SHOP_INSTANCE = 'shop-1';

/** An event as read back from the snapshot (content.events data). Every WRITE
 *  still goes through store.applyOperation, validated by the real EventItem
 *  schema — this is only the narrow read view the panel needs. */
interface EventRec {
  id: string;
  title: string;
  date: string;
  time?: string;
  description?: string;
  image?: { src: string; alt: string };
  ctaLabel?: string;
  ctaUrl?: string;
}
const EVENTS_INSTANCE = 'events-1';

/** A published edition — the browser-only-mode mirror of a content-addressed
 *  release: an immutable snapshot + its content hash, kept in localStorage so
 *  publish + rollback are real (rollback re-hydrates the store from the stored
 *  snapshot, exactly as the server re-points at a prior release). */
interface Edition {
  no: number;
  hash: string;
  ts: number;
  snapshot: ContentSnapshotT;
}
const EDITIONS_KEY = 'veldon-try:editions:v1';
const EDITIONS_CAP = 24;

/** The AI assistant runs PREPARED intents only — each is a real, ordered
 *  sequence of engine operations against the SAME store the visitor uses, so
 *  its edits land in the same history (undo works on them) and cannot fabricate
 *  a capability. Narration is fixed to what each step actually does. */
interface AssistStep {
  say: string;
  run?: (store: DemoStore) => void;
}
interface AssistIntent {
  label: string;
  steps: AssistStep[];
}
const ASSIST_INTENTS: ReadonlyArray<AssistIntent> = [
  {
    label: S['assistant.intent.rewriteHero'],
    steps: [
      { say: S['assistant.rewriteHero.opening'] },
      { say: S['assistant.rewriteHero.changing'], run: (s) => s.setFieldValue('uvod', 'hero-title', 'Poctivá kuchyně nad Brnem') },
    ],
  },
  {
    label: S['assistant.intent.addTerracePhoto'],
    steps: [
      { say: S['assistant.addTerracePhoto.opening'] },
      {
        say: S['assistant.addTerracePhoto.adding'],
        run: (s) => {
          const arr = s.getListField('galerie', 'galerie-images') as Array<{ url: string; alt: string }>;
          if (!arr.some((x) => x.url === '/assets/img/gallery-8.webp')) {
            arr.push({ url: '/assets/img/gallery-8.webp', alt: 'Terasa s výhledem' });
            s.setListField('galerie', 'galerie-images', arr as unknown as JsonValueT[]);
          }
        },
      },
    ],
  },
  {
    label: S['assistant.intent.raiseVealPrice'],
    steps: [
      { say: S['assistant.raiseVealPrice.opening'] },
      {
        say: S['assistant.raiseVealPrice.changing'],
        run: (s) => {
          const data = s.getVenueData('menu-1');
          const cat = data.menus.find((c) => c.id === 'z-ohniste');
          const item = cat?.items.find((i) => i.id === 'zebro');
          if (item) {
            const cur = parseInt(item.price.replace(/[^\d]/g, ''), 10) || 395;
            s.updateMenuItem('menu-1', 'z-ohniste', 'zebro', { price: `${cur + 40} Kč` });
          }
        },
      },
    ],
  },
  {
    label: S['assistant.intent.shortenFriday'],
    steps: [
      { say: S['assistant.shortenFriday.opening'] },
      {
        say: S['assistant.shortenFriday.changing'],
        run: (s) => {
          const data = s.getVenueData('menu-1');
          const rules = data.hours.regular.map((r) =>
            'open' in r && r.days.includes('fri') ? { ...r, close: '23:00' } : r,
          );
          s.setHours('menu-1', rules as never);
        },
      },
    ],
  },
  {
    label: S['assistant.intent.undo'],
    steps: [{ say: S['assistant.undo.running'], run: (s) => s.undo() }],
  },
];

const IMAGE_LIBRARY: ReadonlyArray<CuratedImage> = [
  { src: '/assets/img/gallery-1.webp', thumb: '/assets/img/thumb-1.webp', alt: 'Podávaný chod s vínem' },
  { src: '/assets/img/gallery-2.webp', thumb: '/assets/img/thumb-2.webp', alt: 'Maso z ohniště na prkně' },
  { src: '/assets/img/gallery-3.webp', thumb: '/assets/img/thumb-3.webp', alt: 'Sezónní salát' },
  { src: '/assets/img/gallery-4.webp', thumb: '/assets/img/thumb-4.webp', alt: 'Víno u západu slunce nad kopci' },
  { src: '/assets/img/gallery-5.webp', thumb: '/assets/img/thumb-5.webp', alt: 'Večerní sál restaurace' },
  { src: '/assets/img/gallery-6.webp', thumb: '/assets/img/thumb-6.webp', alt: 'Rybí chod na talíři' },
  { src: '/assets/img/gallery-7.webp', thumb: '/assets/img/thumb-7.webp', alt: 'Prostřený stůl shora' },
  { src: '/assets/img/gallery-8.webp', thumb: '/assets/img/thumb-8.webp', alt: 'Terasa s výhledem' },
  { src: '/assets/img/gallery-9.webp', thumb: '/assets/img/thumb-9.webp', alt: 'Steak s hranolky' },
  { src: '/assets/img/gallery-10.webp', thumb: '/assets/img/thumb-10.webp', alt: 'Přípitek vínem' },
  { src: '/assets/img/gallery-11.webp', thumb: '/assets/img/thumb-11.webp', alt: 'Sdílené jídlo u stolu' },
  { src: '/assets/img/gallery-12.webp', thumb: '/assets/img/thumb-12.webp', alt: 'Interiér restaurace' },
];

const WEEKDAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const WEEKDAY_LABEL: Record<string, string> = {
  mon: S['hours.weekday.mon'],
  tue: S['hours.weekday.tue'],
  wed: S['hours.weekday.wed'],
  thu: S['hours.weekday.thu'],
  fri: S['hours.weekday.fri'],
  sat: S['hours.weekday.sat'],
  sun: S['hours.weekday.sun'],
};
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Minimal Lucide-style inline icons (24x24, 2px stroke, currentColor). */
const ICONS: Record<string, string> = {
  undo: '<path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>',
  redo: '<path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"/>',
  share: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/>',
  rocket: '<path d="M4.5 16.5c-1.5 1.3-2 5-2 5s3.7-.5 5-2c.7-.8.7-2 0-2.8a2 2 0 0 0-3 0Z"/><path d="M12 15l-3-3a22 22 0 0 1 8-10c3 0 5 2 5 5a22 22 0 0 1-10 8Z"/><path d="M9 12H4s.5-2.8 2-4c1.7-1.3 5-1 5-1M12 15v5s2.8-.5 4-2c1.3-1.3 1-5 1-5"/>',
  gift: '<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M5 12v9h14v-9"/><path d="M12 8S10.5 3 7.5 4.5 12 8 12 8ZM12 8s1.5-5 4.5-3.5S12 8 12 8Z"/>',
  image: '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-4.5-4.5L5 21"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  trash: '<path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/>',
  up: '<path d="m18 15-6-6-6 6"/>',
  down: '<path d="m6 9 6 6 6-6"/>',
  x: '<path d="M18 6 6 18M6 6l12 12"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  palette: '<circle cx="12" cy="12" r="9"/><circle cx="7.5" cy="10.5" r="1.2"/><circle cx="12" cy="8" r="1.2"/><circle cx="16.5" cy="10.5" r="1.2"/><path d="M12 21a3 3 0 0 1 0-6 2 2 0 0 0 0-4"/>',
  grid: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  utensils: '<path d="M4 3v7a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3M6 12v9M15 3c-1.5 0-3 1.5-3 5s1.5 4 3 4v9"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  edit: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  lock: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  sparkle: '<path d="M12 3l1.6 4.8L18 9l-4.4 1.2L12 15l-1.6-4.8L6 9l4.4-1.2Z"/><path d="M19 15l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7Z"/>',
  arrowRight: '<path d="M5 12h14M13 6l6 6-6 6"/>',
};

function icon(name: string, size = 20): string {
  const body = ICONS[name] ?? '';
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;
}

/** RFC 4648 base32 decode — the account demo's TOTP secret is a real base32
 *  string (the same you'd paste into an authenticator app). */
const B32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
function base32ToBytes(value: string): Uint8Array {
  const clean = value.toUpperCase().replace(/[^A-Z2-7]/g, '');
  let bits = 0;
  let acc = 0;
  const out: number[] = [];
  for (const ch of clean) {
    acc = (acc << 5) | B32_ALPHABET.indexOf(ch);
    bits += 5;
    if (bits >= 8) { out.push((acc >>> (bits - 8)) & 0xff); bits -= 8; }
  }
  return new Uint8Array(out);
}

/** The REAL TOTP algorithm (RFC 6238, HMAC-SHA1, 30s step) via WebCrypto — so
 *  the demo's 2FA code is genuinely correct for the shown secret, not faked.
 *  Demo secret only; no account exists behind it. */
async function totpCode(secret: Uint8Array, step = 30, digits = 6): Promise<string> {
  const counter = Math.floor(Date.now() / 1000 / step);
  const msg = new ArrayBuffer(8);
  new DataView(msg).setUint32(4, counter);
  const key = await crypto.subtle.importKey('raw', secret as unknown as ArrayBuffer, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, msg));
  const offset = sig[sig.length - 1]! & 0x0f;
  const bin = ((sig[offset]! & 0x7f) << 24) | (sig[offset + 1]! << 16) | (sig[offset + 2]! << 8) | sig[offset + 3]!;
  return (bin % 10 ** digits).toString().padStart(digits, '0');
}
const DEMO_2FA_SECRET = 'JBSWY3DPEHPK3PXP';

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`app.ts: missing #${id} in index.html`);
  return el as T;
}

const ESC_MAP: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ESC_MAP[c] ?? c);
}

/** Substitute {token} placeholders in a catalog string (e.g. S['publish.editionOnline'])
 *  with the given values — the only templating the try editor's UI strings need. */
const fmt = (s: string, vars: Record<string, string | number>): string =>
  s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));

/** Replace an element's children with app-authored markup (all dynamic values
 *  pre-escaped via esc()). Uses insertAdjacentHTML, never innerHTML. */
function setHTML(el: Element, markup: string): void {
  el.replaceChildren();
  el.insertAdjacentHTML('beforeend', markup);
}

/** Build fieldId -> {sectionId, type} across all pattern/freeform manifests. */
function buildFieldMap(def: SiteDefinitionT): Map<string, FieldMeta> {
  const map = new Map<string, FieldMeta>();
  for (const page of def.pages) {
    for (const section of page.sections) {
      if (section.kind === 'module') continue;
      for (const entry of section.editableManifest) map.set(entry.fieldId, { sectionId: section.id, type: entry.type });
    }
  }
  return map;
}

class TryApp {
  private readonly store = new DemoStore({ definition, snapshot });
  private readonly fieldMap = buildFieldMap(definition);
  private frame!: HTMLIFrameElement;
  private frameDoc!: Document;
  private frameWin!: Window & typeof globalThis;
  private firstReveal = true;
  private activePanel = '';
  private scrollRaf = 0;
  private editions: Edition[] = [];
  private livePointer = -1;
  private dirty = false;
  private booted = false;
  private accountTimer = 0;

  constructor() {
    this.frame = byId<HTMLIFrameElement>('frame');
    (window as unknown as { __veldonTry: TryApp; __store: DemoStore }).__veldonTry = this;
    (window as unknown as { __store: DemoStore }).__store = this.store;
  }

  // ===================================================================== boot
  async boot(): Promise<void> {
    this.initFrame();
    this.wireToolbar();
    this.wireRail();
    this.wireKeyboard();
    this.store.onChange(() => {
      this.store.save();
      this.syncToolbar();
      if (this.booted) {
        this.dirty = true;
        this.updateBrowserbar();
      }
    });
    this.loadEditions();

    const hash = location.hash;
    if (hash.length > 3 && (hash.includes('s=') || /^#[gr]/.test(hash))) {
      const shared = await DemoStore.fromShareHash(hash);
      if (shared) {
        this.store.hydrate(shared);
        this.toast(S['toast.sharedVersionLoaded']);
      } else if (!this.store.load()) {
        this.toast(S['toast.invalidShareLink']);
      }
    } else {
      this.store.load();
    }

    this.renderBody();
    this.syncToolbar();
    this.updateBrowserbar();
    this.booted = true;
  }

  // ============================================================ preview iframe
  private initFrame(): void {
    const doc = this.frame.contentDocument;
    if (!doc) throw new Error('preview iframe has no document');
    doc.open();
    doc.write(
      '<!doctype html><html lang="cs" class="hb-js hb-edit-mode" data-theme="light"><head><meta charset="utf-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1">' +
        `<style id="venue-css">${VENUE_CSS}</style></head><body></body></html>`,
    );
    doc.close();
    this.frameDoc = doc;
    this.frameWin = this.frame.contentWindow as Window & typeof globalThis;
    // Module-rendered anchors (an event's Facebook CTA, any absolute URL a
    // visitor enters) must never navigate the preview iframe itself: frameDoc/
    // frameWin are cached once here, so an in-place navigation would leave
    // every later render writing into a detached document — a dead preview.
    // External links open in a new tab; hash anchors keep in-frame scrolling.
    doc.addEventListener('click', (e) => {
      const target = e.target as Element | null;
      const anchor = target?.closest?.('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      if (/^https?:\/\//i.test(href)) {
        e.preventDefault();
        window.open(href, '_blank', 'noopener');
      }
    });
    this.frameWin.addEventListener('scroll', () => {
      if (this.scrollRaf) return;
      this.scrollRaf = this.frameWin.requestAnimationFrame(() => {
        this.scrollRaf = 0;
        const hero = this.frameDoc.querySelector<HTMLElement>('.hb-hero');
        const h = hero ? hero.offsetHeight : 1;
        const p = Math.max(0, Math.min(1, this.frameWin.scrollY / Math.max(1, h)));
        this.frameDoc.documentElement.style.setProperty('--sc', p.toFixed(3));
      });
    });
  }

  /** Re-render the venue from the authoritative snapshot into the preview. */
  private renderBody(): void {
    const html = this.store.renderContent();
    const body = this.frameDoc.body;
    if (html.startsWith('RENDER ERROR')) {
      setHTML(body, `<pre style="padding:2rem;color:#b91c1c;white-space:pre-wrap;font:14px ui-monospace,monospace">${esc(html)}</pre>`);
      return;
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    body.replaceChildren();
    for (const node of Array.from(parsed.body.childNodes)) body.appendChild(this.frameDoc.importNode(node, true));
    this.wireInlineEdits();
    this.wireSectionChips();
    this.wireShop();
    this.wireMobileNav();
    this.wireContactForm();
    this.wireReveal();
  }

  /** Simulated contact submit — no backend: intercept the POST, and on a valid
   *  form reveal the engine-rendered success status (initially hidden) and
   *  retire the form. Re-render restores a fresh form. */
  private wireContactForm(): void {
    const form = this.frameDoc.querySelector<HTMLFormElement>('.ve-forms-contact__form');
    const section = this.frameDoc.querySelector('.ve-forms-contact');
    if (!form || !section) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      const success = section.querySelector<HTMLElement>('.ve-forms-contact__status--success');
      if (success) success.hidden = false;
      form.hidden = true;
    });
  }

  // ============================================================= inline edits
  private wireInlineEdits(): void {
    const nodes = this.frameDoc.querySelectorAll<HTMLElement>('[data-edit]');
    nodes.forEach((node) => {
      const fieldId = node.getAttribute('data-edit');
      if (!fieldId) return;
      const meta = this.fieldMap.get(fieldId);
      if (!meta) return;
      if (meta.type === 'image') {
        if (node.tagName.toLowerCase() !== 'img') return;
        node.addEventListener('click', () => this.openImagePicker(meta.sectionId, fieldId, node as HTMLImageElement));
        node.setAttribute('title', S['inline.imageTooltip']);
        return;
      }
      if (meta.type !== 'text' && meta.type !== 'richtext') return;
      try {
        node.contentEditable = 'plaintext-only';
        if (node.contentEditable !== 'plaintext-only') node.contentEditable = 'true';
      } catch {
        node.contentEditable = 'true';
      }
      node.spellcheck = false;
      const original = node.textContent ?? '';
      node.addEventListener('focus', () => node.classList.add('hb-editing'));
      node.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') {
          e.preventDefault();
          node.blur();
        }
      });
      if (node.tagName.toLowerCase() === 'a') {
        node.addEventListener('click', (e) => e.preventDefault());
      }
      node.addEventListener('blur', () => {
        node.classList.remove('hb-editing');
        const next = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
        if (next === '' || next === original.replace(/\s+/g, ' ').trim()) {
          node.textContent = original;
          return;
        }
        try {
          this.store.setFieldValue(meta.sectionId, fieldId, next);
          this.flash(S['flash.saved']);
        } catch {
          node.textContent = original;
        }
      });
    });
  }

  // ==================================================== section-level editors
  private wireSectionChips(): void {
    const targets: Array<{ anchor: string; label: string; open: string }> = [
      { anchor: 'galerie', label: S['sectionChip.gallery'], open: 'gallery' },
      { anchor: 'menu', label: S['sectionChip.menu'], open: 'menu' },
      { anchor: 'hodiny', label: S['sectionChip.hours'], open: 'hours' },
      { anchor: 'akce', label: S['sectionChip.events'], open: 'events' },
      { anchor: 'obchod', label: S['sectionChip.products'], open: 'products' },
      { anchor: 'kontakt', label: S['sectionChip.contact'], open: 'contact' },
    ];
    for (const t of targets) {
      const sec = this.frameDoc.getElementById(t.anchor);
      if (!sec) continue;
      sec.classList.add('hb-secwrap');
      const btn = this.frameDoc.createElement('button');
      btn.className = 'hb-secedit';
      btn.type = 'button';
      setHTML(btn, `${icon('edit', 14)}${esc(t.label)}`);
      btn.addEventListener('click', () => this.openPanel(t.open));
      sec.appendChild(btn);
    }
  }

  /** Wire the rendered shop's Buy buttons (the module emits inert "Buy" markup —
   *  checkout is server-side in production). Localises the label + opens the
   *  honest browser checkout. */
  private wireShop(): void {
    this.frameDoc.querySelectorAll<HTMLButtonElement>('[data-veldon-buy]').forEach((btn) => {
      btn.textContent = S['shop.buy'];
      const id = btn.getAttribute('data-veldon-buy') ?? '';
      btn.addEventListener('click', () => this.openCheckout(id));
    });
  }

  private wireMobileNav(): void {
    const header = this.frameDoc.querySelector('.ve-header');
    if (!header) return;
    const burger = this.frameDoc.createElement('button');
    burger.className = 'hb-navburger';
    burger.type = 'button';
    burger.setAttribute('aria-label', S['rail.menu']);
    setHTML(burger, '<span></span><span></span><span></span>');
    burger.addEventListener('click', () => this.frameDoc.documentElement.classList.toggle('hb-nav-open'));
    header.appendChild(burger);
    header.querySelectorAll('a').forEach((a) =>
      a.addEventListener('click', () => this.frameDoc.documentElement.classList.remove('hb-nav-open')),
    );
  }

  private wireReveal(): void {
    const items = this.frameDoc.querySelectorAll<HTMLElement>('.hb-hero, .hb-about, .ve-gallery, .ve-venue-menu-hours, .ve-forms-contact');
    if (!this.firstReveal) {
      items.forEach((el) => el.classList.add('hb-reveal', 'hb-in'));
      return;
    }
    this.firstReveal = false;
    const io = new this.frameWin.IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('hb-in');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    items.forEach((el) => {
      el.classList.add('hb-reveal');
      io.observe(el);
    });
    this.frameWin.setTimeout(() => items.forEach((el) => el.classList.add('hb-in')), 1600);
  }

  private flushInline(): void {
    const active = this.frameDoc.activeElement as HTMLElement | null;
    if (active && active.hasAttribute('data-edit')) active.blur();
  }

  // ==================================================================== toolbar
  private wireToolbar(): void {
    byId('act-undo').addEventListener('click', () => {
      this.flushInline();
      this.store.undo();
      this.renderBody();
    });
    byId('act-redo').addEventListener('click', () => {
      this.flushInline();
      this.store.redo();
      this.renderBody();
    });
    byId('act-reset').addEventListener('click', () => this.resetSite());
    byId('act-account').addEventListener('click', () => this.openAccount());
    byId('act-share').addEventListener('click', () => void this.share());
    byId('act-publish').addEventListener('click', () => void this.publish());
  }

  private syncToolbar(): void {
    byId<HTMLButtonElement>('act-undo').disabled = !this.store.canUndo;
    byId<HTMLButtonElement>('act-redo').disabled = !this.store.canRedo;
  }

  /** Discard ALL local edits and clear localStorage — back to the original
   *  fixture. A browser-only-mode affordance (undo can't reach past the reload
   *  boundary), not a CMS operation. */
  private resetSite(): void {
    this.flushInline();
    if (!window.confirm(S['reset.confirm'])) return;
    this.store.reset();
    history.replaceState(null, '', location.pathname + location.search);
    this.renderBody();
    this.closePanel();
    this.toast(S['reset.done']);
  }

  // ======================================================================= rail
  private wireRail(): void {
    document.querySelectorAll<HTMLButtonElement>('#rail [data-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openPanel(btn.getAttribute('data-open') ?? ''));
    });
  }

  // ==================================================================== panels
  private openPanel(kind: string): void {
    this.flushInline();
    this.activePanel = kind;
    const panel = byId('panel');
    const title = byId('panel-title');
    const body = byId('panel-body');
    const meta: Record<string, string> = {
      gallery: S['rail.gallery'],
      menu: S['rail.menu'],
      hours: S['panel.hoursTitle'],
      contact: S['panel.contactTitle'],
      assistant: S['panel.assistantTitle'],
      editions: S['rail.editions'],
      products: S['rail.products'],
      events: S['rail.events'],
    };
    title.textContent = meta[kind] ?? '';
    body.replaceChildren();
    if (kind === 'gallery') this.buildGalleryPanel(body);
    else if (kind === 'menu') this.buildMenuPanel(body);
    else if (kind === 'hours') this.buildHoursPanel(body);
    else if (kind === 'contact') this.buildContactPanel(body);
    else if (kind === 'assistant') this.buildAssistantPanel(body);
    else if (kind === 'editions') this.buildEditionsPanel(body);
    else if (kind === 'products') this.buildProductsPanel(body);
    else if (kind === 'events') this.buildEventsPanel(body);
    panel.classList.add('is-open');
    document.body.classList.add('panel-open');
    this.markRail(kind);
  }

  closePanel(): void {
    byId('panel').classList.remove('is-open');
    document.body.classList.remove('panel-open');
    this.activePanel = '';
    this.markRail('');
  }

  private refreshPanel(): void {
    if (this.activePanel) this.openPanel(this.activePanel);
  }

  private markRail(kind: string): void {
    document.querySelectorAll<HTMLButtonElement>('#rail [data-open]').forEach((b) => {
      b.classList.toggle('is-active', b.getAttribute('data-open') === kind && kind !== '');
    });
  }

  // ---- gallery panel ----
  private buildGalleryPanel(body: HTMLElement): void {
    const images = this.galleryArray();
    const used = new Set(images.map((i) => i.url));
    const list = document.createElement('div');
    list.className = 'p-list';
    images.forEach((img, i) => {
      const row = document.createElement('div');
      row.className = 'p-thumbrow';
      setHTML(
        row,
        `<img src="${esc(img.url.replace('gallery-', 'thumb-'))}" alt="" loading="lazy">` +
          `<span class="p-thumbrow__alt">${esc(img.alt)}</span>` +
          `<span class="p-thumbrow__btns">` +
          `<button class="p-ic" data-a="up" ${i === 0 ? 'disabled' : ''} title="${S['gallery.moveUp']}">${icon('up', 16)}</button>` +
          `<button class="p-ic" data-a="down" ${i === images.length - 1 ? 'disabled' : ''} title="${S['gallery.moveDown']}">${icon('down', 16)}</button>` +
          `<button class="p-ic p-ic--danger" data-a="rm" ${images.length <= 2 ? 'disabled' : ''} title="${S['action.remove']}">${icon('trash', 16)}</button>` +
          `</span>`,
      );
      row.querySelector('[data-a="up"]')?.addEventListener('click', () => this.reorderGallery(i, i - 1));
      row.querySelector('[data-a="down"]')?.addEventListener('click', () => this.reorderGallery(i, i + 1));
      row.querySelector('[data-a="rm"]')?.addEventListener('click', () => this.removeGallery(i));
      list.appendChild(row);
    });
    body.appendChild(list);

    const addable = IMAGE_LIBRARY.filter((i) => !used.has(i.src));
    const addWrap = document.createElement('div');
    addWrap.className = 'p-section';
    setHTML(addWrap, `<p class="p-label">${images.length >= 12 ? S['gallery.libraryHeadingFull'] : S['gallery.libraryHeading']}</p>`);
    const grid = document.createElement('div');
    grid.className = 'p-addgrid';
    addable.forEach((img) => {
      const b = document.createElement('button');
      b.className = 'p-add';
      b.type = 'button';
      b.disabled = images.length >= 12;
      setHTML(b, `<img src="${esc(img.thumb)}" alt="${esc(img.alt)}" loading="lazy"><span>${icon('plus', 16)}</span>`);
      b.addEventListener('click', () => this.addGallery(img));
      grid.appendChild(b);
    });
    if (addable.length === 0) setHTML(grid, `<p class="p-empty">${S['gallery.libraryEmpty']}</p>`);
    addWrap.appendChild(grid);
    body.appendChild(addWrap);
  }

  private galleryArray(): Array<{ url: string; alt: string }> {
    return this.store.getListField('galerie', 'galerie-images') as Array<{ url: string; alt: string }>;
  }
  private commitGallery(next: Array<{ url: string; alt: string }>): void {
    this.store.setListField('galerie', 'galerie-images', next as unknown as JsonValueT[]);
    this.renderBody();
    this.refreshPanel();
  }
  private reorderGallery(from: number, to: number): void {
    const arr = this.galleryArray();
    if (to < 0 || to >= arr.length) return;
    const [moved] = arr.splice(from, 1);
    if (moved) arr.splice(to, 0, moved);
    this.commitGallery(arr);
  }
  private removeGallery(i: number): void {
    const arr = this.galleryArray();
    if (arr.length <= 2) return;
    arr.splice(i, 1);
    this.commitGallery(arr);
  }
  private addGallery(img: CuratedImage): void {
    const arr = this.galleryArray();
    if (arr.length >= 12) return;
    arr.push({ url: img.src, alt: img.alt });
    this.commitGallery(arr);
  }

  // ---- menu panel ----
  private buildMenuPanel(body: HTMLElement): void {
    const data = this.store.getVenueData('menu-1');
    data.menus.forEach((cat) => {
      const block = document.createElement('div');
      block.className = 'p-section';
      setHTML(block, `<p class="p-label">${esc(cat.name)}</p>`);
      cat.items.forEach((item, idx) => {
        const row = document.createElement('div');
        row.className = 'p-item';
        setHTML(
          row,
          `<div class="p-item__fields">` +
            `<input class="p-in p-in--name" value="${esc(item.name)}" placeholder="${S['field.placeholder.name']}">` +
            `<input class="p-in p-in--price" value="${esc(item.price)}" placeholder="${S['field.placeholder.price']}">` +
            `<input class="p-in p-in--desc" value="${esc(item.description ?? '')}" placeholder="${S['field.placeholder.optionalDescription']}">` +
            `</div>` +
            `<div class="p-item__btns">` +
            `<button class="p-ic" data-a="up" ${idx === 0 ? 'disabled' : ''}>${icon('up', 15)}</button>` +
            `<button class="p-ic" data-a="down" ${idx === cat.items.length - 1 ? 'disabled' : ''}>${icon('down', 15)}</button>` +
            `<button class="p-ic p-ic--danger" data-a="rm">${icon('trash', 15)}</button>` +
            `</div>`,
        );
        const nameIn = row.querySelector<HTMLInputElement>('.p-in--name')!;
        const priceIn = row.querySelector<HTMLInputElement>('.p-in--price')!;
        const descIn = row.querySelector<HTMLInputElement>('.p-in--desc')!;
        const commit = () => this.updateMenuItem(cat.id, item.id, nameIn.value, priceIn.value, descIn.value);
        [nameIn, priceIn, descIn].forEach((inp) => inp.addEventListener('change', commit));
        row.querySelector('[data-a="up"]')?.addEventListener('click', () => this.moveMenuItem(cat.id, idx, idx - 1));
        row.querySelector('[data-a="down"]')?.addEventListener('click', () => this.moveMenuItem(cat.id, idx, idx + 1));
        row.querySelector('[data-a="rm"]')?.addEventListener('click', () => this.removeMenuItem(cat.id, item.id));
        block.appendChild(row);
      });
      const add = document.createElement('button');
      add.className = 'p-addbtn';
      add.type = 'button';
      setHTML(add, `${icon('plus', 16)} ${S['menu.addItem']}`);
      add.addEventListener('click', () => this.addMenuItem(cat.id));
      block.appendChild(add);
      body.appendChild(block);
    });
  }

  private updateMenuItem(catId: string, itemId: string, name: string, price: string, desc: string): void {
    if (name.trim() === '' || price.trim() === '') {
      this.toast(S['menu.validation.nameAndPrice']);
      this.refreshPanel();
      return;
    }
    try {
      this.store.updateMenuItem('menu-1', catId, itemId, { name: name.trim(), price: price.trim(), description: desc.trim() });
      this.renderBody();
      this.flash(S['flash.saved']);
    } catch {
      this.toast(S['save.failed']);
      this.refreshPanel();
    }
  }
  private moveMenuItem(catId: string, from: number, to: number): void {
    const data = this.store.getVenueData('menu-1');
    const cat = data.menus.find((c) => c.id === catId);
    if (!cat || to < 0 || to >= cat.items.length) return;
    const ids = cat.items.map((i) => i.id);
    const [moved] = ids.splice(from, 1);
    if (moved) ids.splice(to, 0, moved);
    this.store.reorderMenuItems('menu-1', catId, ids);
    this.renderBody();
    this.refreshPanel();
  }
  private removeMenuItem(catId: string, itemId: string): void {
    this.store.removeMenuItem('menu-1', catId, itemId);
    this.renderBody();
    this.refreshPanel();
  }
  private addMenuItem(catId: string): void {
    this.store.addMenuItem('menu-1', catId, 'Nová položka', '0 Kč');
    this.renderBody();
    this.refreshPanel();
  }

  // ---- hours panel ----
  private buildHoursPanel(body: HTMLElement): void {
    const data = this.store.getVenueData('menu-1');
    const note = document.createElement('p');
    note.className = 'p-help';
    note.textContent = S['hours.help'];
    body.appendChild(note);
    const rows: Array<{ days: string[]; open: HTMLInputElement; close: HTMLInputElement; closed: HTMLInputElement }> = [];
    data.hours.regular.forEach((rule) => {
      const days = rule.days.slice().sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b));
      const isClosed = 'closed' in rule;
      const row = document.createElement('div');
      row.className = 'p-hoursrow';
      setHTML(
        row,
        `<span class="p-hoursrow__days">${days.map((d) => WEEKDAY_LABEL[d]).join(', ')}</span>` +
          `<span class="p-hoursrow__times">` +
          `<input class="p-in p-in--time" type="time" value="${isClosed ? '' : (rule as { open: string }).open}" ${isClosed ? 'disabled' : ''}>` +
          `<span class="p-dash">–</span>` +
          `<input class="p-in p-in--time" type="time" value="${isClosed ? '' : (rule as { close: string }).close}" ${isClosed ? 'disabled' : ''}>` +
          `</span>` +
          `<label class="p-check"><input type="checkbox" ${isClosed ? 'checked' : ''}> ${S['hours.closed']}</label>`,
      );
      const times = row.querySelectorAll<HTMLInputElement>('.p-in--time');
      const openIn = times[0]!;
      const closeIn = times[1]!;
      const closedIn = row.querySelector<HTMLInputElement>('input[type="checkbox"]')!;
      rows.push({ days, open: openIn, close: closeIn, closed: closedIn });
      closedIn.addEventListener('change', () => {
        const off = closedIn.checked;
        openIn.disabled = off;
        closeIn.disabled = off;
        this.commitHours(rows);
      });
      openIn.addEventListener('change', () => this.commitHours(rows));
      closeIn.addEventListener('change', () => this.commitHours(rows));
      body.appendChild(row);
    });
  }

  private commitHours(rows: Array<{ days: string[]; open: HTMLInputElement; close: HTMLInputElement; closed: HTMLInputElement }>): void {
    const rules: JsonValueT[] = [];
    for (const r of rows) {
      if (r.closed.checked) {
        rules.push({ days: r.days, closed: true });
      } else {
        if (!TIME_RE.test(r.open.value) || !TIME_RE.test(r.close.value)) {
          this.toast(S['hours.validation.invalidTime']);
          return;
        }
        rules.push({ days: r.days, open: r.open.value, close: r.close.value });
      }
    }
    try {
      this.store.setHours('menu-1', rules as never);
      this.renderBody();
      this.flash(S['hours.saved']);
    } catch {
      this.toast(S['hours.saveFailed']);
    }
  }

  // ---- contact copy panel ----
  private buildContactPanel(body: HTMLElement): void {
    const snap = this.store.getSnapshot();
    const raw = snap.modules['contact-1']?.data as Record<string, string> | undefined;
    const c = raw ?? {};
    const fields: Array<{ key: string; label: string; area?: boolean }> = [
      { key: 'heading', label: S['contact.field.heading'] },
      { key: 'intro', label: S['contact.field.intro'], area: true },
      { key: 'submitLabel', label: S['contact.field.submitLabel'] },
      { key: 'successMessage', label: S['contact.field.successMessage'] },
      { key: 'consentText', label: S['contact.field.consent'], area: true },
    ];
    fields.forEach((f) => {
      const wrap = document.createElement('label');
      wrap.className = 'p-field';
      const value = esc(String(c[f.key] ?? ''));
      setHTML(
        wrap,
        `<span class="p-field__label">${esc(f.label)}</span>` +
          (f.area ? `<textarea class="p-in" rows="2">${value}</textarea>` : `<input class="p-in" value="${value}">`),
      );
      const input = wrap.querySelector<HTMLInputElement | HTMLTextAreaElement>('.p-in')!;
      input.addEventListener('change', () => {
        const v = input.value.trim();
        if (v === '' && (f.key === 'heading' || f.key === 'submitLabel' || f.key === 'successMessage')) {
          this.toast(S['field.validation.required']);
          this.refreshPanel();
          return;
        }
        try {
          this.store.updateContactCopy('contact-1', { [f.key]: v });
          this.renderBody();
          this.flash(S['flash.saved']);
        } catch {
          this.toast(S['save.failed']);
          this.refreshPanel();
        }
      });
      body.appendChild(wrap);
    });
  }

  // ================================================================ image picker
  private openImagePicker(sectionId: string, fieldId: string, imgEl: HTMLImageElement): void {
    const currentSrc = imgEl.getAttribute('src') ?? '';
    const cards = IMAGE_LIBRARY.map(
      (img) =>
        `<button class="pick" data-src="${esc(img.src)}" data-alt="${esc(img.alt)}"${img.src === currentSrc ? ' aria-current="true"' : ''}>` +
        `<img src="${esc(img.thumb)}" alt="${esc(img.alt)}" loading="lazy">` +
        `<span class="pick__alt">${esc(img.alt)}</span></button>`,
    ).join('');
    this.openModal(
      'image',
      `<div class="modal__head"><h2>${icon('image', 20)} ${S['imagePicker.title']}</h2><button class="modal__x" data-close>${icon('x', 20)}</button></div>` +
        `<div class="pickgrid">${cards}</div>`,
      (root) => {
        root.querySelectorAll<HTMLButtonElement>('.pick').forEach((b) => {
          b.addEventListener('click', () => {
            const src = b.getAttribute('data-src') ?? '';
            const alt = b.getAttribute('data-alt') ?? '';
            try {
              this.store.setImage(sectionId, fieldId, src, alt);
              this.renderBody();
              this.flash(S['imagePicker.changed']);
            } catch {
              this.toast(S['imagePicker.changeFailed']);
            }
            this.closeModal();
          });
        });
      },
    );
  }

  // ============================================================ shop reads + checkout (honest — nothing is charged)
  private shopData(): { currency: string; products: ShopProduct[] } {
    const raw = this.store.getSnapshot().modules[SHOP_INSTANCE]?.data as { currency?: string; products?: ShopProduct[] } | undefined;
    return { currency: raw?.currency ?? 'CZK', products: raw?.products ?? [] };
  }
  private findProduct(id: string): ShopProduct | undefined {
    return this.shopData().products.find((p) => p.id === id);
  }

  /** An honest checkout for any product: a real card form that visibly does not
   *  charge, ending on the truthful "in production this is Stripe Connect on
   *  your own account" state. Gift cards get an amount picker. */
  private openCheckout(productId: string): void {
    const product = this.findProduct(productId);
    if (!product) { this.toast(S['checkout.productNotFound']); return; }
    const isGift = product.kind === 'gift_card';
    const presets = (product.giftCard?.presetAmountsMinor ?? [50000, 100000, 200000]).map((m) => Math.round(m / 100));
    const kc = (n: number): string => fmt(S['currency.formattedKoruna'], { amount: n.toLocaleString('cs-CZ') });
    let amountKc = isGift ? (presets[1] ?? presets[0] ?? 1000) : Math.round(product.price.amountMinor / 100);

    const view = (): string =>
      `<div class="modal__head"><h2>${icon(isGift ? 'gift' : 'grid', 20)} ${esc(product.name)} <span class="pill">${S['demo.badge']}</span></h2><button class="modal__x" data-close>${icon('x', 20)}</button></div>` +
      `<div class="gift">` +
      (isGift
        ? `<div class="giftcard" data-card><div class="giftcard__top"><span>Hřeben</span><span class="giftcard__chip"></span></div>` +
          `<div class="giftcard__amt" data-amt>${kc(amountKc)}</div>` +
          `<div class="giftcard__foot"><span>Dárkový poukaz</span><span>hreben.cz</span></div></div>`
        : `<div class="buyline">${product.image ? `<img src="${esc(product.image.src)}" alt="" loading="lazy">` : ''}` +
          `<div class="buyline__meta"><strong>${esc(product.name)}</strong>${product.description ? `<span>${esc(product.description)}</span>` : ''}</div>` +
          `<span class="buyline__price">${kc(amountKc)}</span></div>`) +
      `<div class="giftform">` +
      (isGift
        ? `<p class="p-label">${S['checkout.giftValue']}</p><div class="chips" data-chips>` +
          presets.map((v) => `<button class="chip${v === amountKc ? ' is-active' : ''}" data-amt-btn="${v}">${kc(v)}</button>`).join('') +
          `<span class="chip chip--custom"><input type="number" min="200" step="100" value="${amountKc}" data-amt-in> Kč</span></div>`
        : '') +
      `<p class="p-label">${S['checkout.cardPayment']}</p>` +
      `<div class="cardbox"><div class="cardbox__badge">${icon('lock', 13)} ${S['checkout.testMode']}</div>` +
      `<div class="cardrow"><input class="p-in cc" data-cc placeholder="${S['checkout.cardNumberPlaceholder']}" inputmode="numeric" maxlength="19" value="${S['checkout.testCardNumber']}">` +
      `<input class="p-in cc cc--sm" data-exp placeholder="${S['checkout.expiryPlaceholder']}" maxlength="5" value="${S['checkout.testExpiry']}"><input class="p-in cc cc--sm" data-cvc placeholder="${S['checkout.cvcPlaceholder']}" maxlength="4" value="${S['checkout.testCvc']}"></div>` +
      `<p class="cardbox__note">${S['checkout.safetyNote']}</p></div>` +
      `<button class="hb-cta-primary giftpay" data-pay>${fmt(S['checkout.pay'], { amount: amountKc.toLocaleString('cs-CZ') })}</button>` +
      `</div></div>`;

    const done = (): string =>
      `<div class="modal__head"><h2>${icon('check', 20)} ${S['checkout.doneTitle']}</h2><button class="modal__x" data-close>${icon('x', 20)}</button></div>` +
      `<div class="roadmap"><div class="roadmap__ring">${icon('check', 34)}</div>` +
      `<h3>${S['checkout.orderAccepted']}</h3>` +
      `<p>${S['checkout.productionExplanation']}</p>` +
      `<p class="roadmap__tag">${icon('lock', 15)} ${S['checkout.featureStatus']}</p>` +
      `<div class="roadmap__btns"><button class="hb-cta-ghost" data-back>${S['action.back']}</button><button class="hb-cta-primary" data-close>${S['checkout.understand']}</button></div></div>`;

    const wire = (root: HTMLElement): void => {
      const rerender = (): void => { setHTML(root, view()); wire(root); };
      root.querySelectorAll<HTMLButtonElement>('[data-amt-btn]').forEach((b) =>
        b.addEventListener('click', () => { amountKc = Number(b.getAttribute('data-amt-btn')); rerender(); }));
      root.querySelector<HTMLInputElement>('[data-amt-in]')?.addEventListener('change', (e) => {
        amountKc = Math.max(200, Number((e.target as HTMLInputElement).value) || 0); rerender();
      });
      const cc = root.querySelector<HTMLInputElement>('[data-cc]');
      cc?.addEventListener('input', () => { const d = cc.value.replace(/\D/g, '').slice(0, 16); cc.value = d.replace(/(.{4})/g, '$1 ').trim(); });
      const exp = root.querySelector<HTMLInputElement>('[data-exp]');
      exp?.addEventListener('input', () => { const d = exp.value.replace(/\D/g, '').slice(0, 4); exp.value = d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; });
      root.querySelector<HTMLButtonElement>('[data-pay]')?.addEventListener('click', (e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        btn.disabled = true; btn.classList.add('is-loading');
        setHTML(btn, `<span class="spin"></span> ${S['checkout.processing']}`);
        window.setTimeout(() => {
          setHTML(root, done());
          root.querySelector('[data-back]')?.addEventListener('click', rerender);
          root.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.closeModal()));
        }, 1100);
      });
    };

    this.openModal('gift', view(), wire);
  }

  // ============================================================ products panel (real commerce ops)
  private buildProductsPanel(body: HTMLElement): void {
    const help = document.createElement('p');
    help.className = 'p-help';
    help.textContent = S['products.help'];
    body.appendChild(help);

    const kc = (minor: number): string => Math.round(minor / 100).toString();
    this.shopData().products.forEach((p) => {
      const row = document.createElement('div');
      row.className = 'p-prodrow';
      setHTML(
        row,
        `<div class="p-prodrow__fields">` +
          `<input class="p-in p-in--name" value="${esc(p.name)}" placeholder="${S['field.placeholder.name']}">` +
          `<span class="p-pricewrap"><input class="p-in p-in--price" inputmode="numeric" value="${esc(kc(p.price.amountMinor))}" ${p.kind === 'gift_card' ? `disabled title="${S['products.giftPriceTooltip']}"` : ''}> Kč</span>` +
          `</div>` +
          `<div class="p-prodrow__btns">` +
          `<label class="p-check p-check--sm"><input type="checkbox" data-a="avail" ${p.available ? 'checked' : ''}> ${S['products.available']}</label>` +
          `<button class="p-ic p-ic--danger" data-a="rm" title="${S['action.remove']}">${icon('trash', 15)}</button>` +
          `</div>`,
      );
      const nameIn = row.querySelector<HTMLInputElement>('.p-in--name')!;
      const priceIn = row.querySelector<HTMLInputElement>('.p-in--price')!;
      const availIn = row.querySelector<HTMLInputElement>('[data-a="avail"]')!;
      const commitFields = (): void => {
        const name = nameIn.value.trim();
        const kcNum = parseInt(priceIn.value.replace(/[^\d]/g, ''), 10);
        if (!name) { this.toast(S['products.validation.emptyName']); this.refreshPanel(); return; }
        const fields: Record<string, unknown> = { name };
        if (p.kind !== 'gift_card' && Number.isFinite(kcNum) && kcNum > 0) fields.price = { amountMinor: kcNum * 100, currency: this.shopData().currency };
        this.runShopOp('updateProduct', { id: p.id, fields });
      };
      nameIn.addEventListener('change', commitFields);
      priceIn.addEventListener('change', commitFields);
      availIn.addEventListener('change', () => this.runShopOp('setAvailable', { id: p.id, available: availIn.checked }));
      row.querySelector('[data-a="rm"]')?.addEventListener('click', () => this.runShopOp('removeProduct', { id: p.id }));
      body.appendChild(row);
    });

    const add = document.createElement('form');
    add.className = 'p-section p-addprod';
    setHTML(
      add,
      `<p class="p-label">${S['products.addHeading']}</p>` +
        `<input class="p-in" data-new-name maxlength="80" placeholder="${S['products.namePlaceholder']}" autocomplete="off">` +
        `<span class="p-pricewrap"><input class="p-in" data-new-price inputmode="numeric" maxlength="7" placeholder="${S['field.placeholder.price']}" autocomplete="off"> Kč</span>` +
        `<button class="p-addbtn" type="submit">${icon('plus', 16)} ${S['products.addToShop']}</button>`,
    );
    add.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = (add.querySelector<HTMLInputElement>('[data-new-name]')!.value || '').trim().slice(0, 80);
      const kcNum = parseInt((add.querySelector<HTMLInputElement>('[data-new-price]')!.value || '').replace(/[^\d]/g, ''), 10);
      if (!name || !Number.isFinite(kcNum) || kcNum <= 0) { this.toast(S['products.validation.newProduct']); return; }
      this.runShopOp('addProduct', {
        id: this.uniqueSlug(name, new Set(this.shopData().products.map((p) => p.id))),
        name,
        price: { amountMinor: kcNum * 100, currency: this.shopData().currency },
        available: true,
        kind: 'standard',
      });
    });
    body.appendChild(add);
  }

  /** Dispatch a commerce.catalog operation on the shop instance, then re-render
   *  the preview + panel. A rejected op (engine validation) surfaces as a toast;
   *  nothing mutates. */
  private runShopOp(op: string, input: unknown): void {
    try {
      this.store.applyOperation(SHOP_INSTANCE, op, input);
      this.renderBody();
      this.refreshPanel();
      this.flash(S['flash.saved']);
    } catch {
      this.toast(S['save.failed']);
      this.refreshPanel();
    }
  }

  /** A URL-safe unique slug from a display name, disambiguated against a set of
   *  ids already in use (shared by the product + event add flows). */
  private uniqueSlug(name: string, existing: Set<string>): string {
    // The trailing-hyphen trim must run AFTER the length cut: slicing at 40 can
    // land on a word separator and re-introduce a trailing '-', which the Slug
    // schema rejects — a valid long title would then fail to add.
    const base = name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '') || 'polozka';
    if (!existing.has(base)) return base;
    for (let n = 2; n < 999; n += 1) { const candidate = `${base}-${n}`.slice(0, 44); if (!existing.has(candidate)) return candidate; }
    return `${base}-${Date.now()}`;
  }

  // ============================================================ events panel (real content.events ops)
  private eventsList(): EventRec[] {
    const raw = this.store.getSnapshot().modules[EVENTS_INSTANCE]?.data as { events?: EventRec[] } | undefined;
    return raw?.events ?? [];
  }

  private runEventsOp(op: string, input: unknown): void {
    try {
      this.store.applyOperation(EVENTS_INSTANCE, op, input);
      this.renderBody();
      this.refreshPanel();
      this.flash(S['flash.saved']);
    } catch {
      this.toast(S['save.failed']);
      this.refreshPanel();
    }
  }

  private buildEventsPanel(body: HTMLElement): void {
    const help = document.createElement('p');
    help.className = 'p-help';
    help.textContent = S['events.help'];
    body.appendChild(help);

    this.eventsList().forEach((ev) => {
      const row = document.createElement('div');
      row.className = 'p-eventrow';
      const photoBlock = ev.image
        ? `<img class="p-eventthumb" src="${esc(ev.image.src.replace('gallery-', 'thumb-'))}" alt="">` +
          `<button class="p-linkbtn" data-a="photo" type="button">${S['events.changePhoto']}</button>` +
          `<button class="p-linkbtn p-linkbtn--muted" data-a="photo-rm" type="button">${S['events.removePhoto']}</button>`
        : `<button class="p-linkbtn" data-a="photo" type="button">${icon('image', 14)} ${S['events.addOptionalPhoto']}</button>`;
      setHTML(
        row,
        `<div class="p-eventrow__top">` +
          `<input class="p-in p-in--title" value="${esc(ev.title)}" placeholder="${S['events.titlePlaceholder']}">` +
          `<button class="p-ic p-ic--danger" data-a="rm" title="${S['events.removeEvent']}">${icon('trash', 15)}</button>` +
          `</div>` +
          `<div class="p-eventrow__meta">` +
          `<input class="p-in p-in--date" type="date" value="${esc(ev.date)}">` +
          `<input class="p-in p-in--time" type="time" value="${esc(ev.time ?? '')}">` +
          `</div>` +
          `<textarea class="p-in p-in--desc" rows="2" placeholder="${S['field.placeholder.optionalDescription']}">${esc(ev.description ?? '')}</textarea>` +
          `<div class="p-eventrow__photo">${photoBlock}</div>` +
          `<div class="p-eventrow__link">` +
          `<input class="p-in p-in--ctalabel" value="${esc(ev.ctaLabel ?? '')}" placeholder="${S['events.ctaLabelPlaceholder']}" maxlength="60">` +
          `<input class="p-in p-in--ctaurl" value="${esc(ev.ctaUrl ?? '')}" placeholder="${S['events.ctaUrlPlaceholder']}" inputmode="url">` +
          `</div>`,
      );
      const titleIn = row.querySelector<HTMLInputElement>('.p-in--title')!;
      const dateIn = row.querySelector<HTMLInputElement>('.p-in--date')!;
      const timeIn = row.querySelector<HTMLInputElement>('.p-in--time')!;
      const descIn = row.querySelector<HTMLTextAreaElement>('.p-in--desc')!;
      const labelIn = row.querySelector<HTMLInputElement>('.p-in--ctalabel')!;
      const urlIn = row.querySelector<HTMLInputElement>('.p-in--ctaurl')!;
      const commitText = (): void => {
        const title = titleIn.value.trim();
        const date = dateIn.value;
        if (!title || !date) { this.toast(S['events.validation.requiredExisting']); this.refreshPanel(); return; }
        this.runEventsOp('updateEvent', {
          id: ev.id,
          fields: { title, date, time: timeIn.value || null, description: descIn.value.trim() || null },
        });
      };
      const commitLink = (): void => {
        const label = labelIn.value.trim();
        let url = urlIn.value.trim();
        if (label === (ev.ctaLabel ?? '') && url === (ev.ctaUrl ?? '')) return;
        if (!label && !url) { this.runEventsOp('updateEvent', { id: ev.id, fields: { ctaLabel: null, ctaUrl: null } }); return; }
        // Half-filled pair: toast WITHOUT rebuilding the panel — a rebuild here
        // would wipe the half the visitor just typed.
        if (!label || !url) { this.toast(S['events.validation.linkPair']); return; }
        if (!/^(https?:\/\/|\/|#|mailto:|tel:)/i.test(url)) url = `https://${url}`;
        this.runEventsOp('updateEvent', { id: ev.id, fields: { ctaLabel: label, ctaUrl: url } });
      };
      [titleIn, dateIn, timeIn, descIn].forEach((el) => el.addEventListener('change', commitText));
      // The label+URL are ONE edit: committing on each input's `change` would
      // fire on a half-typed pair and rebuild the panel, discarding the other
      // half mid-entry (adding or removing a link would be impossible through
      // the UI). Commit only when focus leaves the whole group.
      const linkGroup = row.querySelector<HTMLElement>('.p-eventrow__link')!;
      linkGroup.addEventListener('focusout', (e) => {
        const next = e.relatedTarget as Node | null;
        if (next && linkGroup.contains(next)) return;
        commitLink();
      });
      row.querySelector('[data-a="photo"]')?.addEventListener('click', () => this.openEventImagePicker(ev.id));
      row.querySelector('[data-a="photo-rm"]')?.addEventListener('click', () => this.runEventsOp('updateEvent', { id: ev.id, fields: { image: null } }));
      row.querySelector('[data-a="rm"]')?.addEventListener('click', () => this.runEventsOp('removeEvent', { id: ev.id }));
      body.appendChild(row);
    });

    const add = document.createElement('form');
    add.className = 'p-section p-addevent';
    setHTML(
      add,
      `<p class="p-label">${S['events.add']}</p>` +
        `<input class="p-in" data-new-title maxlength="120" placeholder="${S['events.titlePlaceholder']}" autocomplete="off">` +
        `<input class="p-in" data-new-date type="date">` +
        `<button class="p-addbtn" type="submit">${icon('plus', 16)} ${S['events.add']}</button>`,
    );
    add.addEventListener('submit', (e) => {
      e.preventDefault();
      const title = (add.querySelector<HTMLInputElement>('[data-new-title]')!.value || '').trim().slice(0, 120);
      const date = add.querySelector<HTMLInputElement>('[data-new-date]')!.value;
      if (!title || !date) { this.toast(S['events.validation.newEvent']); return; }
      this.runEventsOp('addEvent', { id: this.uniqueSlug(title, new Set(this.eventsList().map((e) => e.id))), title, date });
    });
    body.appendChild(add);
  }

  /** Pick an event's photo from the curated library (module data, so it commits
   *  through the real updateEvent op, not the content-value setImage path). */
  private openEventImagePicker(eventId: string): void {
    const cards = IMAGE_LIBRARY.map(
      (img) =>
        `<button class="pick" data-src="${esc(img.src)}" data-alt="${esc(img.alt)}"><img src="${esc(img.thumb)}" alt="${esc(img.alt)}" loading="lazy"><span class="pick__alt">${esc(img.alt)}</span></button>`,
    ).join('');
    this.openModal(
      'image',
      `<div class="modal__head"><h2>${icon('image', 20)} ${S['events.imagePickerTitle']}</h2><button class="modal__x" data-close>${icon('x', 20)}</button></div>` +
        `<div class="pickgrid">${cards}</div>`,
      (root) => {
        root.querySelectorAll<HTMLButtonElement>('.pick').forEach((b) => {
          b.addEventListener('click', () => {
            const src = b.getAttribute('data-src') ?? '';
            const alt = b.getAttribute('data-alt') ?? '';
            this.closeModal();
            this.runEventsOp('updateEvent', { id: eventId, fields: { image: { src, alt } } });
          });
        });
      },
    );
  }

  // ============================================================ account + 2FA (REAL TOTP, demo secret)
  private openAccount(): void {
    const secret = base32ToBytes(DEMO_2FA_SECRET);
    // The key itself renders in a <code> pill, so the catalog string carries a
    // {key} placeholder and the markup is assembled around it (a <code> pill
    // cannot ride through a plain fmt() substitution).
    const authKeyDisplay = DEMO_2FA_SECRET.replace(/(.{4})/g, '$1 ').trim();
    const [authKeyBefore = '', authKeyAfter = ''] = S['account.authenticatorKey'].split('{key}');
    const shell = (code: string): string =>
      `<div class="modal__head"><h2>${icon('lock', 20)} ${S['account.title']} <span class="pill">${S['demo.badge']}</span></h2><button class="modal__x" data-close>${icon('x', 20)}</button></div>` +
      `<div class="acct">` +
      `<div class="acct__card"><div class="acct__cardtop"><span>Hřeben</span><span class="acct__cardchip"></span></div>` +
      `<p class="acct__k">${S['account.twoFactorCode']}</p>` +
      `<div class="acct__coderow"><span class="acct__code" data-code>${esc(code)}</span>` +
      `<span class="acct__ring"><svg viewBox="0 0 36 36" aria-hidden="true"><circle class="acct__ringbg" cx="18" cy="18" r="15.5"/><circle class="acct__ringfg" data-ring cx="18" cy="18" r="15.5" pathLength="100" stroke-dasharray="100" stroke-dashoffset="0"/></svg><span class="acct__secs" data-secs>${fmt(S['account.codeCountdown'], { seconds: 30 })}</span></span></div>` +
      `</div>` +
      `<p class="acct__secret">${esc(authKeyBefore)}<code>${esc(authKeyDisplay)}</code>${esc(authKeyAfter)}</p>` +
      `<p class="acct__note">${S['account.totpExplanation']}</p>` +
      `<div class="acct__methods"><span>${icon('lock', 13)} ${S['account.method.password']}</span><span>${icon('check', 13)} ${S['account.method.twoFactor']}</span><span>${icon('mail', 13)} ${S['account.method.google']}</span></div>` +
      `<p class="acct__foot">${S['account.backendNote']}</p>` +
      `</div>`;

    this.openModal('account', shell(S['account.codeLoading']), (root) => {
      const tick = async (): Promise<void> => {
        const inWindow = (Date.now() / 1000) % 30;
        const code = await totpCode(secret);
        const codeEl = root.querySelector('[data-code]');
        if (codeEl) codeEl.textContent = fmt(S['account.codeFormat'], { code3a: code.slice(0, 3), code3b: code.slice(3) });
        const ring = root.querySelector('[data-ring]');
        if (ring) ring.setAttribute('stroke-dashoffset', String((inWindow / 30) * 100));
        const secsEl = root.querySelector('[data-secs]');
        if (secsEl) secsEl.textContent = fmt(S['account.codeCountdown'], { seconds: Math.max(1, Math.ceil(30 - inWindow)) });
      };
      void tick();
      if (this.accountTimer) window.clearInterval(this.accountTimer);
      this.accountTimer = window.setInterval(() => void tick(), 1000);
    });
  }

  // =============================================================== publish + editions (REAL)
  /** Publish the current snapshot as an immutable, content-addressed edition
   *  (localStorage). Rollback re-hydrates the store from a stored edition — the
   *  browser-only mirror of re-pointing the live release. */
  private async publish(): Promise<void> {
    this.flushInline();
    const snap = this.store.getSnapshot();
    const hash = `vd:${await this.contentHash(snap)}`;
    const liveHash = this.livePointer >= 0 ? this.editions[this.livePointer]?.hash : undefined;
    if (hash === liveHash && !this.dirty) {
      this.toast(S['publish.noChanges']);
      return;
    }
    const no = this.editions.length + 1;
    this.editions.push({ no, hash, ts: Date.now(), snapshot: snap });
    if (this.editions.length > EDITIONS_CAP) this.editions = this.editions.slice(-EDITIONS_CAP);
    this.livePointer = this.editions.length - 1;
    this.dirty = false;
    this.saveEditions();
    this.updateBrowserbar();
    this.showPublished(no, hash);
  }

  private showPublished(no: number, hash: string): void {
    const previousIndex = this.livePointer - 1;
    const previousEdition = previousIndex >= 0 ? this.editions[previousIndex] : undefined;
    const rollbackMoment = previousEdition
      ? `<div class="pubdone__rollback"><p>${S['publish.rollbackExplanation']}</p>` +
        `<button class="pubdone__rollback-action" type="button" data-rollback-previous>${S['publish.rollbackAction']}</button>` +
        `<p class="pubdone__rollback-result" data-rollback-result hidden></p></div>`
      : '';
    const body =
      `<div class="pubdone"><div class="pubdone__ring">${icon('check', 36)}</div>` +
      `<p class="pubdone__k">${fmt(S['publish.editionOnline'], { n: no })}</p>` +
      `<h2 class="pubdone__title">${S['publish.title']}</h2>` +
      `<p class="pubdone__hash">${icon('lock', 13)} ${esc(fmt(S['publish.hashAndDomain'], { hash }))}</p>` +
      `<p class="pubdone__note">${S['publish.explanation']}</p>` +
      rollbackMoment +
      `<div class="product-handoff">${this.productHandoffContents()}</div>` +
      `<div class="pubdone__btns"><button class="hb-cta-ghost" data-editions>${S['publish.showEditions']}</button><button class="hb-cta-primary" data-close>${S['action.done']}</button></div></div>`;
    this.openModal('publish', body, (root) => {
      root.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.closeModal()));
      root.querySelector<HTMLButtonElement>('[data-rollback-previous]')?.addEventListener('click', (event) => {
        const result = root.querySelector<HTMLElement>('[data-rollback-result]');
        if (!previousEdition || !result) return;
        this.rollbackToEdition(previousIndex, S['publish.rollbackDone']);
        result.textContent = fmt(S['publish.rollbackDone'], { n: previousEdition.no });
        result.hidden = false;
        (event.currentTarget as HTMLButtonElement).disabled = true;
      });
      root.querySelector('[data-editions]')?.addEventListener('click', () => {
        this.closeModal();
        this.openPanel('editions');
      });
    });
  }

  private productHandoffContents(): string {
    return `<h3 class="product-handoff__heading">${S['handoff.heading']}</h3>` +
      `<p class="product-handoff__text">${S['handoff.text']}</p>` +
      `<div class="product-handoff__actions">` +
      `<a class="product-handoff__cta hb-cta-primary" href="https://api.veldon.cz/signup" target="_blank" rel="noopener">${S['handoff.startFree']}</a>` +
      `<a class="product-handoff__link" href="https://edit.veldon.cz">${S['handoff.howItWorks']}</a></div>`;
  }

  private buildEditionsPanel(body: HTMLElement): void {
    const help = document.createElement('p');
    help.className = 'p-help';
    help.textContent = this.editions.length === 0 ? S['editions.emptyHelp'] : S['editions.help'];
    body.appendChild(help);
    if (this.editions.length === 0) {
      this.appendProductHandoff(body);
      return;
    }

    const list = document.createElement('div');
    list.className = 'edlist';
    for (let i = this.editions.length - 1; i >= 0; i -= 1) {
      const ed = this.editions[i]!;
      const isLive = i === this.livePointer;
      const row = document.createElement('div');
      row.className = `edrow${isLive ? ' is-live' : ''}`;
      setHTML(
        row,
        `<span class="edrow__no">${fmt(S['editions.number'], { n: ed.no })}</span>` +
          `<span class="edrow__meta"><span class="edrow__hash">${esc(ed.hash)}</span><span class="edrow__time">${esc(fmt(S['editions.time'], { time: this.fmtTime(ed.ts) }))}</span></span>` +
          `<span class="edrow__state">${isLive ? S['editions.state.live'] : S['editions.state.edition']}</span>` +
          `<button class="edrow__btn" type="button"${isLive ? ' disabled' : ''}>${S['editions.restore']}</button>`,
      );
      row.querySelector('button')?.addEventListener('click', () => this.rollbackToEdition(i));
      list.appendChild(row);
    }
    body.appendChild(list);
    this.appendProductHandoff(body);
  }

  private appendProductHandoff(body: HTMLElement): void {
    const handoff = document.createElement('div');
    handoff.className = 'product-handoff';
    setHTML(handoff, this.productHandoffContents());
    body.appendChild(handoff);
  }

  private rollbackToEdition(i: number, restoredMessage: string = S['editions.restored']): void {
    const ed = this.editions[i];
    if (!ed || i === this.livePointer) return;
    this.flushInline();
    this.store.hydrate(ed.snapshot);
    this.livePointer = i;
    this.dirty = false;
    this.saveEditions();
    this.renderBody();
    this.updateBrowserbar();
    this.refreshPanel();
    this.toast(fmt(restoredMessage, { n: ed.no }));
  }

  private fmtTime(ts: number): string {
    try {
      return new Date(ts).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  private updateBrowserbar(): void {
    const host = document.getElementById('bb-state');
    if (!host) return;
    host.className = 'browserbar__state';
    if (this.dirty || this.livePointer < 0) {
      host.classList.add('is-draft');
      host.textContent = this.livePointer < 0 ? S['browserState.draft'] : S['browserState.unsaved'];
    } else {
      host.classList.add('is-live');
      host.textContent = fmt(S['browserState.live'], { n: this.editions[this.livePointer]!.no });
    }
  }

  private async contentHash(snap: ContentSnapshotT): Promise<string> {
    const json = JSON.stringify(snap);
    if (window.crypto?.subtle && location.protocol === 'https:') {
      try {
        const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(json));
        return Array.from(new Uint8Array(buf))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')
          .slice(0, 7);
      } catch {
        /* fall through to FNV */
      }
    }
    let h = 0x811c9dc5;
    for (let i = 0; i < json.length; i += 1) {
      h ^= json.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0').slice(0, 7);
  }

  private loadEditions(): void {
    try {
      const raw = localStorage.getItem(EDITIONS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { editions?: unknown; livePointer?: unknown };
      if (Array.isArray(parsed.editions)) {
        this.editions = parsed.editions as Edition[];
        this.livePointer = typeof parsed.livePointer === 'number' ? parsed.livePointer : -1;
      }
    } catch {
      /* corrupt store — start clean */
    }
  }

  private saveEditions(): void {
    const write = (): void => {
      localStorage.setItem(EDITIONS_KEY, JSON.stringify({ editions: this.editions, livePointer: this.livePointer }));
    };
    try {
      write();
    } catch {
      // quota — drop the oldest half and retry once
      try {
        this.editions = this.editions.slice(-8);
        this.livePointer = Math.min(this.livePointer, this.editions.length - 1);
        write();
      } catch {
        /* give up silently — editions are a convenience, not the source of truth */
      }
    }
  }

  // =============================================================== AI assistant (the second pen)
  private buildAssistantPanel(body: HTMLElement): void {
    const intro = document.createElement('div');
    intro.className = 'asst__intro';
    setHTML(
      intro,
      `<p class="asst__lead">${S['assistant.intro']}</p>` +
        `<span class="asst__chiplabel">${icon('sparkle', 13)} ${S['assistant.demoLabel']}</span>`,
    );
    body.appendChild(intro);

    const chips = document.createElement('div');
    chips.className = 'asst__chips';
    ASSIST_INTENTS.forEach((it) => {
      const b = document.createElement('button');
      b.className = 'asst__chip';
      b.type = 'button';
      b.textContent = it.label;
      b.addEventListener('click', () => void this.runIntent(it, b));
      chips.appendChild(b);
    });
    body.appendChild(chips);

    const log = document.createElement('div');
    log.className = 'asst__log';
    log.id = 'asst-log';
    log.setAttribute('aria-live', 'polite');
    body.appendChild(log);
  }

  private async runIntent(intent: AssistIntent, btn: HTMLButtonElement): Promise<void> {
    const log = document.getElementById('asst-log');
    if (!log) return;
    const chipButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.asst__chip'));
    chipButtons.forEach((b) => (b.disabled = true));
    btn.classList.add('is-running');

    const line = document.createElement('div');
    line.className = 'asst__line';
    setHTML(line, `<span class="asst__who">${S['assistant.userLabel']}</span><span class="asst__said">${esc(intent.label)}</span>`);
    log.appendChild(line);
    const steps = document.createElement('div');
    steps.className = 'asst__steps';
    log.appendChild(steps);

    try {
      for (const step of intent.steps) {
        const s = document.createElement('div');
        s.className = 'asst__step';
        s.textContent = step.say;
        steps.appendChild(s);
        log.scrollTop = log.scrollHeight;
        await this.delay(560);
        step.run?.(this.store); // REAL engine op(s) on the same store
        this.renderBody();
        s.classList.add('is-done');
      }
      const done = document.createElement('div');
      done.className = 'asst__step asst__step--final';
      done.textContent = S['assistant.completed'];
      steps.appendChild(done);
      this.flash(S['assistant.changedSite']);
    } catch {
      const err = document.createElement('div');
      err.className = 'asst__step asst__step--err';
      err.textContent = S['assistant.unsupported'];
      steps.appendChild(err);
    } finally {
      chipButtons.forEach((b) => (b.disabled = false));
      btn.classList.remove('is-running');
      log.scrollTop = log.scrollHeight;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  // ==================================================================== modal
  private openModal(kind: string, html: string, wire?: (root: HTMLElement) => void): void {
    const host = byId('modal');
    setHTML(host, `<div class="modal__scrim" data-close></div><div class="modal__card modal__card--${kind}" role="dialog" aria-modal="true">${html}</div>`);
    host.classList.add('is-open');
    document.body.classList.add('modal-open');
    const card = host.querySelector<HTMLElement>('.modal__card')!;
    host.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.closeModal()));
    if (wire) wire(card);
  }

  private closeModal(): void {
    if (this.accountTimer) { window.clearInterval(this.accountTimer); this.accountTimer = 0; }
    const host = byId('modal');
    host.classList.remove('is-open');
    host.replaceChildren();
    document.body.classList.remove('modal-open');
  }

  // ===================================================================== share
  private async share(): Promise<void> {
    this.flushInline();
    const hash = await this.store.toShareHash();
    if (hash === null) {
      this.exportFile();
      this.toast(S['share.exportFallback']);
      return;
    }
    const url = `${location.origin}${location.pathname}#s=${hash}`;
    history.replaceState(null, '', `#s=${hash}`);
    try {
      await navigator.clipboard.writeText(url);
      this.toast(S['share.copied']);
    } catch {
      this.toast(S['share.copyManually']);
    }
  }

  private exportFile(): void {
    const blob = new Blob([JSON.stringify(this.store.getSnapshot(), null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = S['share.exportFilename'];
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ================================================================== keyboard
  private wireKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      const meta = e.ctrlKey || e.metaKey;
      if (e.key === 'Escape') {
        if (byId('modal').classList.contains('is-open')) this.closeModal();
        else if (this.activePanel) this.closePanel();
        return;
      }
      if (!meta) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault();
        this.flushInline();
        this.store.undo();
        this.renderBody();
      } else if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault();
        this.flushInline();
        this.store.redo();
        this.renderBody();
      }
    });
  }

  // ===================================================================== toast
  private toast(message: string): void {
    const host = byId('toast');
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    host.appendChild(el);
    requestAnimationFrame(() => el.classList.add('show'));
    window.setTimeout(() => {
      el.classList.remove('show');
      window.setTimeout(() => el.remove(), 300);
    }, 2600);
  }

  /** Tiny, non-blocking "saved" flash on the stage. */
  private flash(message: string): void {
    const el = byId('flash');
    el.textContent = message;
    el.classList.add('show');
    const holder = el as unknown as { _t: number };
    window.clearTimeout(holder._t);
    holder._t = window.setTimeout(() => el.classList.remove('show'), 1100);
  }
}

const app = new TryApp();
byId('panel-close').addEventListener('click', () => app.closePanel());
void app.boot();
